import { useEffect, type ReactNode } from 'react';

declare global {
  interface Window {
    __BASE_PATH__?: string;
  }
}

interface SeoProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  jsonLd?: object | object[];
}

function buildCanonical(path: string): string {
  const base = window.__BASE_PATH__ || '';
  const host = window.location.origin;
  return `${host}${base}${path === '/' ? '/' : path}`;
}

export default function Seo({
  title,
  description,
  keywords,
  canonicalPath = '/',
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    document.title = title;

    const ensureMeta = (name: string, content: string, attr: 'name' | 'property') => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    ensureMeta('description', description, 'name');
    if (keywords) ensureMeta('keywords', keywords, 'name');

    // canonical
    let canon = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canon) {
      canon = document.createElement('link');
      canon.rel = 'canonical';
      document.head.appendChild(canon);
    }
    canon.href = buildCanonical(canonicalPath);

    // JSON-LD
    const target = document.getElementById('seo-jsonld') as HTMLScriptElement | null;
    if (target && jsonLd) {
      target.textContent = JSON.stringify(jsonLd);
    }
  }, [title, description, keywords, canonicalPath, jsonLd]);

  return (
    <script
      id="seo-jsonld"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLd ? JSON.stringify(jsonLd) : '' }}
    />
  ) as unknown as ReactNode;
}